import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Select, { MultiValue, SingleValue } from 'react-select'

import { useAppDispatch } from '../../redux/store'
import i18n from '../../i18n'
import { formatNormLabel } from '../../utils/normLabels'
import {
  fetchSpecificationsAction,
  updateSpecificationAction,
} from '../../redux/specification/specificationAction'
import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import UseAlert from '../../components/common/useAlerte'
import { Specification } from '../../types/specification'
import { useNormCategories } from '../../hooks/useNormCategories'
import { useOnboarding } from '../../features/onboarding/useOnboarding'
import { CURRENCY_CODES, getCurrencyLabel } from '../../constants/currencies'
import { DetailButton } from '../../components/common/detail'
import {
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormNote,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import './Settings.css'

const NORM_OPTIONS = [
  { value: 'NIST', label: 'NIST', short: 'NIST', hintKey: 'nist' },
  {
    value: 'ISO_13',
    label: 'ISO 27001:2013',
    short: 'ISO 2013',
    hintKey: 'iso13',
  },
  {
    value: 'ISO_22',
    label: 'ISO 27001:2022',
    short: 'ISO 2022',
    hintKey: 'iso22',
  },
  { value: 'NIS2', label: 'NIS2', short: 'NIS2', hintKey: 'nis2' },
  { value: 'SOC', label: 'SOC', short: 'SOC', hintKey: 'soc' },
  { value: 'SWIFT', label: 'SWIFT', short: 'SWIFT', hintKey: 'swift' },
  { value: 'SAMA', label: 'SAMA', short: 'SAMA', hintKey: 'sama' },
] as const

type Option = { value: string; label: string }

function Settings() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const loading = useSelector(selectSpecificationLoading)
  const settings = useSelector(selectSpecifications)
  const { industrialApplicableFrameworks } = useNormCategories()
  const { restartTour } = useOnboarding()

  const [settingsData, setSettingsData] = useState<Specification>({
    NormBase: '',
    Currency: '',
    IndustrialApplicableFrameworks: [],
  })
  const [savedSnapshot, setSavedSnapshot] = useState<Specification | null>(null)
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle')

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
    status: 0,
  })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const currencyMenu = useExclusiveMenu()
  const frameworkMenu = useExclusiveMenu()

  useEffect(() => {
    dispatch(fetchSpecificationsAction())
  }, [dispatch])

  useEffect(() => {
    if (settings && settings.length > 0) {
      const next = {
        NormBase: settings[0].NormBase || '',
        Currency: settings[0].Currency || '',
        IndustrialApplicableFrameworks:
          settings[0].IndustrialApplicableFrameworks || [],
      }
      setSettingsData(next)
      setSavedSnapshot(next)
    }
  }, [settings])


  const currencyOptions = useMemo<Option[]>(
    () =>
      CURRENCY_CODES.map((code) => ({
        value: code,
        label: getCurrencyLabel(code, i18n.language),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  )

  const applicableOptions = useMemo<Option[]>(
    () =>
      (industrialApplicableFrameworks || []).map((opt) => ({
        value: opt,
        label: formatNormLabel(opt),
      })),
    [industrialApplicableFrameworks]
  )

  const currencyValue =
    currencyOptions.find((opt) => opt.value === settingsData.Currency) || null
  const applicableValue = applicableOptions.filter((opt) =>
    (settingsData.IndustrialApplicableFrameworks || []).includes(opt.value)
  )

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return false
    const a = settingsData
    const b = savedSnapshot
    const frameworksA = [...(a.IndustrialApplicableFrameworks || [])].sort()
    const frameworksB = [...(b.IndustrialApplicableFrameworks || [])].sort()
    return (
      a.NormBase !== b.NormBase ||
      a.Currency !== b.Currency ||
      frameworksA.join(',') !== frameworksB.join(',')
    )
  }, [settingsData, savedSnapshot])

  const isComplete = Boolean(
    settingsData.NormBase &&
      settingsData.Currency &&
      (settingsData.IndustrialApplicableFrameworks || []).length > 0
  )

  const handleReset = () => {
    if (savedSnapshot) setSettingsData(savedSnapshot)
    setSaveStatus('idle')
  }

  const editSettings = (
    updater: (current: Specification) => Specification
  ) => {
    setSaveStatus('idle')
    setSettingsData(updater)
  }

  const handleSave = async () => {
    if (!settings[0]?._id) return
    setSaveStatus('saving')
    try {
      await dispatch(
        updateSpecificationAction({
          id: settings[0]._id as string,
          settings: settingsData,
        })
      ).unwrap()
      setSavedSnapshot(settingsData)
      setSaveStatus('saved')
      setAlert({
        open: true,
        message: t('settingsPage.saveSuccess'),
        severity: 'success',
        status: 200,
      })
    } catch (error) {
      setSaveStatus('error')
      setAlert({
        open: true,
        message:
          (error as { message?: string })?.message ||
          t('settingsPage.saveError'),
        severity: 'error',
        status: 400,
      })
    }
  }

  const summaryNorm = settingsData.NormBase
    ? formatNormLabel(settingsData.NormBase)
    : ''
  const summaryCurrency = currencyValue?.label || ''
  const summaryFrameworks = applicableValue.map((o) => o.label)

  const statusLabel = !isComplete
    ? t('settingsPage.status.incomplete')
    : saveStatus === 'saving'
      ? t('settingsPage.status.saving')
      : saveStatus === 'error'
        ? t('settingsPage.status.saveFailed')
        : isDirty || saveStatus !== 'saved'
          ? isDirty
            ? t('settingsPage.status.unsaved')
            : ''
          : t('settingsPage.status.saved')

  const statusDotClass = !isComplete
    ? ''
    : saveStatus === 'error' || isDirty
      ? 'is-dirty'
      : saveStatus === 'saved'
        ? 'is-ready'
        : ''

  return (
    <div className="page-content" ref={containerRef}>
      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
      />

      <FormPageHead
        eyebrow={t('settingsPage.eyebrow')}
        title={t('settingsPage.title')}
        subtitle={t('settingsPage.instruction')}
        backLabel={t('buttons.back', { defaultValue: 'Back' })}
        onBack={() => navigate('/')}
        titleTourId="settings-page-title"
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('settingsPage.sections.currency.title')}
              description={t('settingsPage.sections.currency.desc')}
            >
              <FormGrid cols={1}>
                <FormField
                  label={t('settingsPage.currencyLabel')}
                  required
                  help={t('settingsPage.sections.currency.help')}
                >
                  <Select
                    classNamePrefix="incyia-settings"
                    value={currencyValue}
                    options={currencyOptions}
                    isSearchable
                    placeholder={t('selectNormPage.currencyLabel')}
                    noOptionsMessage={() => t('tables.noData')}
                    {...currencyMenu}
                    styles={incyiaSelectStyles}
                    onChange={(selected: SingleValue<Option>) =>
                      editSettings((p) => ({
                        ...p,
                        Currency: selected?.value || '',
                      }))
                    }
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('settingsPage.sections.norm.title')}
              description={t('settingsPage.sections.norm.desc')}
            >
              <FormGrid cols={1}>
                <FormNote>{t('settingsPage.sections.norm.note')}</FormNote>
                <FormField
                  label={t('settingsPage.selectNorm')}
                  required
                  span
                >
                  <div
                    className="incyia-settings-norm-grid"
                    role="radiogroup"
                    aria-label={t('settingsPage.selectNorm')}
                  >
                    {NORM_OPTIONS.map((norm) => {
                      const active = settingsData.NormBase === norm.value
                      return (
                        <button
                          key={norm.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          aria-label={norm.label}
                          title={norm.label}
                          className={`incyia-settings-norm-card${
                            active ? ' is-active' : ''
                          }`}
                          onClick={() =>
                            editSettings((p) => ({
                              ...p,
                              NormBase: norm.value,
                            }))
                          }
                        >
                          <span className="incyia-settings-norm-card-mark">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 12 12"
                              fill="none"
                              aria-hidden
                            >
                              <path
                                d="M2.5 6.2L4.8 8.5 9.5 3.5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className="incyia-settings-norm-card-copy">
                            <span className="incyia-settings-norm-card-label">
                              {norm.short}
                            </span>
                            <span className="incyia-settings-norm-card-hint">
                              {t(`settingsPage.normHints.${norm.hintKey}`)}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('settingsPage.sections.frameworks.title')}
              description={t('settingsPage.sections.frameworks.desc')}
            >
              <FormGrid cols={1}>
                <FormField
                  label={t('settingsPage.selectApplicable')}
                  required
                  help={t('settingsPage.sections.frameworks.help')}
                >
                  <Select
                    classNamePrefix="incyia-settings"
                    isMulti
                    value={applicableValue}
                    options={applicableOptions}
                    isSearchable
                    placeholder={t('settingsPage.selectApplicable')}
                    noOptionsMessage={() => t('tables.noData')}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    {...frameworkMenu}
                    styles={incyiaSelectStyles}
                    onChange={(selected: MultiValue<Option>) =>
                      editSettings((p) => ({
                        ...p,
                        IndustrialApplicableFrameworks: selected.map(
                          (item) => item.value
                        ),
                      }))
                    }
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={
                isDirty
                  ? t('settingsPage.foot.unsaved')
                  : t('settingsPage.foot.hint')
              }
              cancelLabel={t('settingsPage.resetButton')}
              submitLabel={t('settingsPage.saveButton')}
              onCancel={handleReset}
              onSubmit={handleSave}
              submitDisabled={
                loading || saveStatus === 'saving' || !isComplete || !isDirty
              }
            />
          </FormShell>
        }
        aside={
          <div className="incyia-form-aside">
            <div className="incyia-aside-card">
              <h4>{t('settingsPage.aside.snapshot')}</h4>
              <div className="incyia-aside-body">
                <div className="incyia-settings-summary">
                  <div className="incyia-settings-summary-row">
                    <span className="incyia-settings-summary-k">
                      {t('settingsPage.aside.currency')}
                    </span>
                    <span
                      className={`incyia-settings-summary-v${
                        !summaryCurrency ? ' incyia-settings-summary-empty' : ''
                      }`}
                    >
                      {summaryCurrency || t('settingsPage.aside.notSet')}
                    </span>
                  </div>
                  <div className="incyia-settings-summary-row">
                    <span className="incyia-settings-summary-k">
                      {t('settingsPage.aside.norm')}
                    </span>
                    <span
                      className={`incyia-settings-summary-v${
                        !summaryNorm ? ' incyia-settings-summary-empty' : ''
                      }`}
                    >
                      {summaryNorm || t('settingsPage.aside.notSet')}
                    </span>
                  </div>
                  <div className="incyia-settings-summary-row">
                    <span className="incyia-settings-summary-k">
                      {t('settingsPage.aside.frameworks')}
                    </span>
                    {summaryFrameworks.length ? (
                      <div className="incyia-settings-chips">
                        {summaryFrameworks.map((label) => (
                          <span key={label} className="incyia-settings-chip">
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="incyia-settings-summary-v incyia-settings-summary-empty">
                        {t('settingsPage.aside.notSet')}
                      </span>
                    )}
                  </div>
                </div>
                {statusLabel ? (
                  <div className="incyia-settings-status">
                    <span
                      className={`incyia-settings-status-dot ${statusDotClass}`}
                    />
                    {statusLabel}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="incyia-aside-card">
              <h4>{t('settingsPage.aside.help')}</h4>
              <div className="incyia-aside-body">
                <p className="incyia-aside-note">
                  {t('settingsPage.aside.tourHint')}
                </p>
                <div className="incyia-settings-aside-actions">
                  <DetailButton
                    variant="ghost"
                    className="incyia-settings-tour-btn"
                    onClick={restartTour}
                    data-tour-id="restart-onboarding-button"
                  >
                    {t('onboarding.restart')}
                  </DetailButton>
                </div>
              </div>
            </div>

            <div className="incyia-aside-card">
              <h4>{t('settingsPage.aside.goodToKnow')}</h4>
              <div className="incyia-aside-body">
                <p className="incyia-aside-note">{t('settingsPage.aside.tip')}</p>
              </div>
            </div>
          </div>
        }
      />
    </div>
  )
}

export default Settings
